<template>
    <div class="card" style="min-height: calc(100vh - 180px)">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="false"
                :showAddNew="false"
                title="Purchase Return Approval"
            />
        </div>

        <div class="px-2">
            <DateQuerySetter
                btnTitle="Go"
                @onClickGo="getPurchaseReturnList"
            >
                <div class="col-12 col-sm-6 col-md-4 col-lg-2">
                    <label for="colFormLabel" class="col-form-label">Bill Number: </label>
                    <input
                        placeholder="Bill Number"
                        v-model="billNumber"
                        class="form-control"
                    />
                </div>
            </DateQuerySetter>
        </div>

        <div class="d-flex justify-content-end px-2 mt-1">
            <button
                class="btn btn-success"
                :disabled="disableApproveBtn"
                @click="handleApproval"
            >
                Approve Selected
            </button>
        </div>

        <div class="col-12 px-2 mt-1">
            <ApprovalListTable :purchaseReturn="purchaseReturnList" @onView="onViewPurchaseReturn"/>
        </div>

        <div class="px-2">
            <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <PurchaseReturnDetailModal
            v-if="selectedPurchaseReturnId"
            :purchase-return-id="selectedPurchaseReturnId"
            :approving="singleApproving"
            @onApprove="handleSingleApproval"
            @onClose="closeDetailModal"
        />

        <GlobalLoader/>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import GlobalLoader from '@/components/atom/GlobalLoader'
import ApprovalListTable from './ApprovalListTable.vue'
import PurchaseReturnDetailModal from './PurchaseReturnDetailModal.vue'
import handlePurchaseAndSalesReturn from '@/services/modules/purchaseSalesReturn'

const $router = useRouter()
const $route = useRoute()
const $store = useStore()
const showSuccess = inject('showSuccess')
const showError = inject('showError')

const offset = ref(20)
const purchaseReturnList = ref([])
const billNumber = ref('')
const selectedPurchaseReturnId = ref(null)
const singleApproving = ref(false)

const { fetchPurchaseReturnListByQuery, approvePurchaseReturn } = handlePurchaseAndSalesReturn()

const companyId = computed(() => $route.params.companyId)

const disableApproveBtn = computed(() => {
    return purchaseReturnList.value.filter(item => item.selected === true).length === 0
})

const getQuery = () => {
    let query = '?company_id=' + companyId.value
    query += '&offset=' + offset.value
    query += '&start_date=' + $route.query.start
    query += '&end_date=' + $route.query.end
    query += '&status=pending_approval'
    if (billNumber.value) query += '&bill_number=' + billNumber.value
    if ($route.query.page) query += '&page=' + $route.query.page
    return query
}

const getPurchaseReturnList = () => {
    fetchPurchaseReturnListByQuery(getQuery())
        .then(res => {
            if (!res.status) {
                purchaseReturnList.value = []
                $store.commit('resetPagination')
                return
            }
            purchaseReturnList.value = res.date.data
            setPagination(res.date)
        })
        .catch(err => {
            showError(err.response.message)
        })
}

function setPagination(data) {
    if (!data.data.length) return
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, $route.query)
    routeQuery.page = page
    await $router.push({ path: $route.path, query: routeQuery })
    await getPurchaseReturnList()
}

const handleApproval = () => {
    const purchaseReturnIds = purchaseReturnList.value
        .filter(item => item.selected === true)
        .map(item => item.id)

    if (!purchaseReturnIds.length) {
        showError('Please select at least one purchase return')
        return
    }

    const confirmed = window.confirm('Are you sure you want to approve the selected purchase return(s)?')

    if (!confirmed) return

    const data = {
        company_id: companyId.value,
        purchase_return_ids: purchaseReturnIds
    }

    $store.state.loading = true

    approvePurchaseReturn(data)
        .then(res => {
            if (!res.status) {
                showError(res.message)
                return
            }
            showSuccess(res.message)
            getPurchaseReturnList()
        })
        .finally(() => ($store.state.loading = false))
}

const onViewPurchaseReturn = (item) => {
    selectedPurchaseReturnId.value = item.id
}

const closeDetailModal = () => {
    selectedPurchaseReturnId.value = null
}

const handleSingleApproval = (purchaseReturnId) => {
    const confirmed = window.confirm('Are you sure you want to approve this purchase return?')

    if (!confirmed) return

    const data = {
        company_id: companyId.value,
        purchase_return_ids: [purchaseReturnId]
    }

    singleApproving.value = true

    approvePurchaseReturn(data)
        .then(res => {
            if (!res.status) {
                showError(res.message)
                return
            }
            showSuccess(res.message)
            closeDetailModal()
            getPurchaseReturnList()
        })
        .finally(() => (singleApproving.value = false))
}

onMounted(() => {
    getPurchaseReturnList()
})
</script>
